import { Link } from '@tanstack/react-router'
import { CalendarClock } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'

/**
 * The `/meet/$code` link works before the meeting starts (PLAN-085 §1.4).
 * You get a countdown, the notes document to prepare in, and the option to
 * go in early anyway.
 */
export function WaitingScreen({
  projectId,
  title,
  startAt,
  opensAt,
  notesDocumentId,
  onJoin,
}: {
  projectId: string
  title: string
  startAt: string | null
  /** When the room opens (start − JOIN_LEAD_MS); the countdown targets this. */
  opensAt: string | null
  notesDocumentId: string | null
  onJoin: () => void
}) {
  const { t } = useTranslation('meet')
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1_000)
    return () => clearInterval(id)
  }, [])

  const targetMs = Date.parse(opensAt ?? startAt ?? '')
  const remaining = Number.isNaN(targetMs) ? null : targetMs - now

  return (
    <div className="flex h-full flex-1 items-center justify-center p-10">
      <div className="flex max-w-sm flex-col items-center gap-3 text-center">
        <CalendarClock className="text-muted-foreground/60 h-8 w-8" />
        <h1 className="text-lg font-semibold">{title}</h1>
        <p className="text-muted-foreground text-sm">{t('waiting.title')}</p>
        {remaining !== null && (
          <p className="text-sm font-medium tabular-nums">
            {remaining > 0
              ? t('waiting.opensIn', { time: formatRemaining(remaining) })
              : t('waiting.openingNow')}
          </p>
        )}
        <div className="mt-2 flex gap-2">
          {/* The room really is shut until then; this only re-asks the server. */}
          <Button variant="outline" onClick={onJoin}>
            {t('waiting.retry')}
          </Button>
          {notesDocumentId && (
            <Button asChild variant="outline">
              <Link to="/$projectId/write/$docId" params={{ projectId, docId: notesDocumentId }}>
                {t('waiting.notes')}
              </Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

/** `1 t 05 min` / `04:31` — seconds only matter in the last hour. */
export function formatRemaining(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  if (hours > 0)
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}
